import { useEffect, useRef } from 'react';

import { LubavSessionType, LUBAV_SUPERSESSIONS } from '@backrs_ui/grow-ui/src/models/models';

import { getOfficeHoursPromptOptions } from './officeHoursPrompts';
import { PromptOption } from './PromptButtons';

type LubavLaunchParams = Readonly<{
  activeSession: LubavSessionType | null;
  sessionId: number | undefined;
  isReady: boolean;
  isGuest: boolean;
  isHighSchool: boolean;
  onTopic: (option: PromptOption) => void;
  onQuery: (message: string) => void;
}>;

// Open Lubav from URL params: `?q=` sends a first message, `?topic=` opens a topic button.
export const useLubavLaunchParams = ({
  activeSession,
  sessionId,
  isReady,
  isGuest,
  isHighSchool,
  onTopic,
  onQuery,
}: LubavLaunchParams) => {
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    if (activeSession !== LUBAV_SUPERSESSIONS.OFFICE_HOURS) return;
    if (!sessionId || !isReady) return;

    const params = new URLSearchParams(window.location.search);
    const topic = params.get('topic');
    const query = params.get('q')?.trim();

    if (!topic && !query) return;

    handledRef.current = true;

    // strip the params so a refresh doesn't replay the launch
    params.delete('topic');
    params.delete('q');
    const search = params.toString();

    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`,
    );

    // a recognized topic wins; q only runs as a fallback when the topic is unrecognized
    if (topic) {
      const option = getOfficeHoursPromptOptions(activeSession, isHighSchool, isGuest)?.find(
        (o) => o.id === topic || o.sessionId === topic,
      );

      if (option) {
        onTopic(option);

        return;
      }

      console.warn(`Lubav launch: unrecognized topic "${topic}"`);
    }

    if (query) {
      onQuery(query);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSession, sessionId, isReady]);
};
